// =============================================================================
// MeuDriver — Hook: useInstructorVehicles (Batch R2)
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { InstructorVehicleRow, InstructorVehicleInsert, InstructorVehicleUpdate } from '@/types/database'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const vehicleKeys = {
  byInstructor: (instructorId: string) => ['instructor_vehicles', instructorId] as const,
}

// ---------------------------------------------------------------------------
// useInstructorVehicles
// Quando instructorId é omitido, usa o instrutor do usuário autenticado
// ---------------------------------------------------------------------------

export function useInstructorVehicles(instructorId?: string) {
  const { user } = useAuth()

  return useQuery<InstructorVehicleRow[]>({
    queryKey: vehicleKeys.byInstructor(instructorId ?? user?.id ?? ''),
    enabled: !!(instructorId || user?.id),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      let query = supabase
        .from('instructor_vehicles')
        .select('*')
        .order('created_at', { ascending: false })

      if (instructorId) {
        query = query.eq('instructor_id', instructorId)
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as InstructorVehicleRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useCreateVehicle
// ---------------------------------------------------------------------------

export function useCreateVehicle() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (vehicle: Omit<InstructorVehicleInsert, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('instructor_vehicles')
        .insert(vehicle)
        .select()
        .single()

      if (error) throw error
      return data as InstructorVehicleRow
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.byInstructor(data.instructor_id) })
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: vehicleKeys.byInstructor(user.id) })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateVehicle
// ---------------------------------------------------------------------------

export function useUpdateVehicle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      instructorId,
      ...update
    }: InstructorVehicleUpdate & { id: string; instructorId: string }) => {
      const { data, error } = await supabase
        .from('instructor_vehicles')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as InstructorVehicleRow
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.byInstructor(data.instructor_id) })
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteVehicle
// ---------------------------------------------------------------------------

export function useDeleteVehicle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, instructorId }: { id: string; instructorId: string }) => {
      const { error } = await supabase
        .from('instructor_vehicles')
        .delete()
        .eq('id', id)

      if (error) throw error
      return { id, instructorId }
    },
    onSuccess: ({ instructorId }) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.byInstructor(instructorId) })
    },
  })
}
